/* eslint-disable complexity */
import { EventEmitter } from "events";
import { map, throttle, uniqueId } from "lodash";
import { FocusSDK } from "@/utils";
import Axios from "axios";

export enum UseCheckType {
  IMAGE = "image",
  API = "api"
}

interface Optional {
  id?: string;
  url?: string;
  time?: number;
  testFrequency?: number;
  checkType?: UseCheckType;
  notChangeNavigatorOnline?: boolean;
}

// const REQUEST_TIMEOUT = 10000;
const useNavigator = {
  onLine: true
};

const setNavigatorOnline = (currentOnline: boolean) => {
  FocusSDK.printLog(
    `更新window.navigator.onLine: ${window.navigator.onLine}, currentOnline: ${currentOnline}, ${location.href}`
  );
  if (window.navigator.onLine === currentOnline) {
    return;
  }
  if ((window.navigator as any).__defineGetter__) {
    (window.navigator as any).__defineGetter__("onLine", function () {
      return currentOnline;
    });
  } else if (Object.defineProperty) {
    Object.defineProperty(window.navigator, "onLine", {
      get: function () {
        return currentOnline;
      }
    });
  }
};

export { setNavigatorOnline, useNavigator };

/**
 * 网络检测
 */
class NetworkCheck extends EventEmitter {
  private static instances: NetworkCheck[] = [];
  public static getInstance(props?: Optional): NetworkCheck {
    let instance: NetworkCheck | undefined;
    if (props?.id) {
      instance = this.getById(props?.id);
    } else if (props?.url) {
      instance = this.getByUrl(props?.url);
    }
    if (!instance) {
      instance = new NetworkCheck(props);
      FocusSDK.printLog(
        `networkCheck--create`,
        `id: ${instance.id}--props.id: ${props?.id}--props.url: ${props?.url}`
      );
      this.instances.push(instance);
    }
    return instance;
  }
  public static getById(id: string): NetworkCheck | undefined {
    return this.instances?.find((i) => i.id === id);
  }

  private static getByUrl(url: string): NetworkCheck | undefined {
    return this.instances?.find((i) => i.url === url);
  }
  private id: string;
  private url: string;
  private inited: boolean;
  private checkStart: boolean;
  private stopCheckFlag: boolean;
  private time: number;
  private timer: any;
  private testFrequency: number;
  private checkType: UseCheckType;
  private handlerFired: boolean;
  private fileSize = 3000;
  private w: any;
  private handleStateChange: Function;
  private checkPromiseResult: any;
  private notChangeNavigatorOnline: boolean;
  private netCheckThrottleTime: number;
  private lastCheckTime: number;
  private lastCheckResult: number;
  private lastNavigatorOnline: boolean;
  constructor(props?: Optional) {
    super();
    const { url, time, testFrequency, checkType, notChangeNavigatorOnline } = props || {};
    this.url = url || "";
    this.time = time || 1 * 30 * 1000;
    this.testFrequency = testFrequency || 1;
    this.checkType = checkType || UseCheckType.API;
    this.handlerFired = false;
    this.w = window;
    this.handleStateChange = this.getState.bind(this);
    this.id = uniqueId();
    this.inited = false;
    this.checkStart = false;
    this.stopCheckFlag = false;
    this.netCheckThrottleTime = this.getNetCheckThrottleTime();
    this.notChangeNavigatorOnline = !!notChangeNavigatorOnline;
    this.lastCheckTime = 0;
    this.lastCheckResult = 0;
    this.lastNavigatorOnline = window.navigator.onLine;
    this.init();
  }
  public getId(): string {
    return this.id;
  }

  public setStatus(newState: boolean): void {
    this.eventStatus(newState);
    this.w[this.id + "_onLine"] = newState;
  }

  /**
   * 检测网络
   * @returns
   */
  public async check(): Promise<number> {
    if (this.url) {
      try {
        let results: number[] = Array(this.testFrequency).fill(0);
        const isFirefox = /firefox/i.test(navigator.userAgent);
        const navigatorOnline = window.navigator.onLine;
        if (isFirefox && !navigatorOnline) {
          return 0;
        }
        if (
          this.lastCheckTime !== 0 &&
          Date.now() - this.lastCheckTime <= this.netCheckThrottleTime &&
          navigatorOnline === this.lastNavigatorOnline &&
          this.lastCheckResult !== 0
        ) {
          return this.lastCheckResult;
        }
        if (!this.checkPromiseResult) {
          this.checkPromiseResult = Promise.all(
            map(results, async (item, index) => {
              const result =
                this.checkType === UseCheckType.IMAGE
                  ? this.withImgTag(this.url, this.fileSize, index)
                  : this.withRequest(this.url);
              return result;
            })
          );
        }
        results = await this.checkPromiseResult;
        this.checkPromiseResult = null;
        const finalValue =
          results.reduce((p, c) => {
            let current = p;
            current += c;
            return current;
          }) / this.testFrequency;
        this.lastCheckResult = finalValue;
        return finalValue;
      } catch (error) {
        this.checkPromiseResult = null;
        // console.error(`check---error--`, error);
        this.lastCheckResult = 0;
        return 0;
      } finally {
        this.lastCheckTime = Date.now();
        this.lastNavigatorOnline = window.navigator.onLine;
      }
    }
    return 0;
  }

  public async checkState(): Promise<boolean> {
    return this.verifyStatus(await this.check());
  }

  // 通过IM更新网络检测结果
  public upadteNetResult(imStateObj: any) {
    const { time, state } = imStateObj;
    if (!this.lastCheckTime || this.lastCheckTime - time < 0) {
      this.lastCheckTime = time;
      this.lastCheckResult = state;
    }
  }

  /**
   * 图片检测
   * @param imgUrl
   * @param fileSize
   * @returns
   */
  public withImgTag(imgUrl: string, fileSize: number, index: number): Promise<number> {
    return new Promise((resolve, reject) => {
      const imgEl = document.createElement("img");
      const start = new Date().getTime();
      let end = null;
      imgEl.onerror = (e) => reject(e);
      imgEl.onload = () => {
        end = new Date().getTime();
        const speed = fileSize / (end - start);
        resolve(speed);
      };
      imgEl.src = `${imgUrl}?v=${start + index}`;
    });
  }

  /**
   * http请求检测
   * @param url
   * @returns
   */
  public async withRequest(url: string): Promise<number> {
    // const CancelToken = Axios.CancelToken;
    // const source = CancelToken.source();
    // let timer = setTimeout(() => {
    //     clearTimeout(timer);
    //     source.cancel(`请求超时`);
    // }, REQUEST_TIMEOUT);
    try {
      const response = await Axios.get(`${url}?ver=${new Date().getTime()}`, {
        // timeout: REQUEST_TIMEOUT,
        // cancelToken: source.token,
      });
      if (response.status === 200) {
        const data = response.data;
        if (typeof data === "string" && data.indexOf("pong") > -1) {
          return 1;
        }
      }
      return 0;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  public bindEvent(obj: any, type: string, cb: Function): void {
    obj.addEventListener(type, cb);
  }

  public unbindEvent(obj: any, type: string, cb: Function): void {
    obj.removeEventListener(type, cb);
  }

  /**
   * 开始检测
   */
  public async startCheck(): Promise<void> {
    if (!this.inited) {
      return;
    }
    this.clearCheck();
    this.checkStart = true;
    this.stopCheckFlag = false;
    console.log("setStatus startCheck");
    this.setStatus(this.verifyStatus(await this.check()));
    // 停止检测网络时，可能还在等待异步操作结果
    if (!this.stopCheckFlag) {
      this.timer = setTimeout(async () => {
        clearTimeout(this.timer);
        this.startCheck();
      }, this.time);
    }
  }

  /**
   * 重置检测
   * @param time
   */
  public async resetCheck(time: number): Promise<void> {
    this.time = time;
  }

  public clearCheck = () => {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.checkStart = false;
  };

  public resetStartState = () => {
    this.w[this.id + "_onLine"] = window.navigator.onLine;
  };

  public stopCheck(): void {
    this.clearCheck();
    this.stopCheckFlag = true;
    // this.w[this.id + '_onLine'] = window.navigator.onLine;
    this.handlerFired = window.navigator.onLine;
  }

  public isStartCheck(): boolean {
    return this.checkStart;
  }

  /**
   * 检测当前网络状态并返回
   */
  public async checkNet(): Promise<boolean> {
    const result = this.verifyStatus(await this.check());
    console.log(`检测当前网络状态: ${result}`);
    return result;
  }

  /**
   * 获取状态
   */
  public async getState(): Promise<void> {
    if (!this.url) {
      this.setStatus(window.navigator.onLine);
      this.setOnlineStatus(window.navigator.onLine);
    } else {
      this.setStatus(this.verifyStatus(await this.check()));
    }
  }

  /**
   *
   * @returns 初始化
   */
  private async init(): Promise<void> {
    if (this.inited) {
      return;
    }
    this.inited = true;
    this.w[this.id + "_onLine"] = true; // 默认为true，通过接口判断初始状态
    this.handlerFired = this.w[this.id + "_onLine"];
    this.unbindEvent(this.w, "offline", this.handleStateChange);
    this.unbindEvent(this.w, "online", this.handleStateChange);
    this.bindEvent(this.w, "online", this.handleStateChange);
    this.bindEvent(this.w, "offline", this.handleStateChange);
    FocusSDK.printLog(
      `---初始化更新navigator.onLine---${JSON.stringify((FocusSDK as any).windowInfo)}`
    );
    this.getState();
    return new Promise((resolve, reject) => {
      if (!this.url) {
        resolve(true);
      }
      if (this.checkType === UseCheckType.IMAGE) {
        Axios.get(`${this.url}?ver=${new Date().getTime()}`, {
          responseType: "blob"
        })
          .then((res: any) => {
            if (res?.data?.size) {
              this.fileSize = res?.data?.size;
            }
            resolve(true);
          })
          .catch(() => {
            resolve(false);
          });
      } else {
        resolve(true);
      }
    }).then(() => {});
  }

  private eventStatus(newState: boolean): void {
    if (!this.notChangeNavigatorOnline) {
      setNavigatorOnline(newState);
    }
    console.log("newState--", newState, this.w[this.id + "_onLine"], this.url);
    // 去掉handlerFired，仅通过网络变化判断
    if (newState === true && this.w[this.id + "_onLine"] !== true) {
      console.log("emit online", this.url);
      this.emit(`online`, newState);
    }
    if (newState === false && this.w[this.id + "_onLine"] !== false) {
      console.log("emit offline", this.url);
      this.emit(`offline`, newState);
      this.handlerFired = false;
      return;
    }
    this.handlerFired = true;
  }

  /**
   * 检测状态
   * @param reslut
   * @returns
   */
  private verifyStatus(reslut: number): boolean {
    console.log();
    const newStatus = reslut > 0;
    this.setOnlineStatus(newStatus);
    return newStatus;
  }
  private setOnlineStatus(newStatus: boolean): boolean {
    console.log("setOnlineStatusonline111222", newStatus, this.w[this.id + "_onLineStatus"]);
    if (!this.notChangeNavigatorOnline) {
      useNavigator.onLine = newStatus;
    }
    if (newStatus === true && this.w[this.id + "_onLineStatus"] !== true) {
      this.emit(`online-event`, newStatus);
    }
    if (newStatus === false && this.w[this.id + "_onLineStatus"] !== false) {
      this.emit(`offline-event`, newStatus);
    }
    this.w[this.id + "_onLineStatus"] = newStatus;
    return newStatus;
  }

  private getNetCheckThrottleTime() {
    const netCheckConfig = FocusSDK.getNetCheckConfig();
    return netCheckConfig?.throttleTime || 10000;
  }
}

export default NetworkCheck;
